import React, { useEffect, useState } from "react";
import { Stack, Button } from "@mui/material";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import PatientsInformationTable from "../../../../components/PatientsInformationTable/PatientsInformationTable";
import { extractKeyValuePairs, isEmpty, isNull, isUndefined } from "../../../../utils/utils";
import { recordFieldMapping } from "../../../../utils/constants";
import { STORAGE_KEYS, getDataLocally } from "../../../../utils/localStorage";

const EXCLUDED_FIELDS = new Set(["created_date", "isMultipleUpload", "status", "riskScore"]);

const buildPatientDataTemplate = (fieldMapping, key) => {
  const data = { created_date: new Date(), isMultipleUpload: 0, status: 0, riskScore: 0 };
  Object.keys(fieldMapping).forEach((field) => {
    if (!EXCLUDED_FIELDS.has(field)) data[field] = -1;
  });
  return { data, key };
};

const StaticIndividualPatientSection = ({ fieldMapping = recordFieldMapping, decimalFields, submitRecordsFn }) => {
  const [addPatientDataRows, setAddPatientDataRows] = useState([]);
  const [invalidAddRowKeys, setInvalidAddRowKeys] = useState([]);
  const [patientDataArr, setPatientDataArr] = useState([]);
  const getLoggedInOrganization = getDataLocally(STORAGE_KEYS.LOGIN_ORGANIZATION);

  const desired_record_fields = Object.keys(fieldMapping);
  const desiredFieldsAfterSubmission = [...desired_record_fields, "status", "riskScore", "status_message"];

  useEffect(() => {
    handleAddRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddRecord = () => {
    setAddPatientDataRows([
      ...addPatientDataRows,
      buildPatientDataTemplate(fieldMapping, generateUniqueKey(addPatientDataRows.length)),
    ]);
  };

  const isValidatedData = () => {
    let invalidRows = [];
    addPatientDataRows.forEach((row) => {
      const rowData = row.data;
      const rowHasInvalidField = Object.values(rowData).some((value) => {
        const hasNoEntry = value === -1 || isEmpty(value) || isUndefined(value);
        return hasNoEntry || isNaN(value);
      });
      if (rowHasInvalidField) invalidRows.push(row.key);
    });
    setInvalidAddRowKeys(invalidRows);
    return invalidRows.length === 0;
  };

  const onAddAddPatientDataFormChange = (updatedFormData) => {
    const keyToUpdate = updatedFormData.key;
    let newAddPatientDataArr = [...addPatientDataRows];
    newAddPatientDataArr.forEach((row) => {
      if (row.key === keyToUpdate) row = updatedFormData.data;
    });
    setAddPatientDataRows(newAddPatientDataArr);
  };

  async function submitRecords(records, originalDataSet) {
    const formData = new FormData();
    formData.append('records', JSON.stringify(records));
    formData.append('org_id', getLoggedInOrganization.org_id);
    formData.append('user_id', getLoggedInOrganization.user_id);
    if (submitRecordsFn) {
      await submitRecordsFn(formData).then((res) => {
        if (!isNull(res.body)) {
          const dataSetToDisplay = processResponseAfterSubmission(res.body.data, originalDataSet);
          setPatientDataArr(dataSetToDisplay);
          setAddPatientDataRows([]);
        }
      });
    } else {
      console.log('Individual records payload:', records);
      console.log('org_id:', getLoggedInOrganization.org_id, 'user_id:', getLoggedInOrganization.user_id);
    }
  }

  function processResponseAfterSubmission(records, originalDataSet) {
    originalDataSet.forEach((dataSet) => {
      records.forEach((record) => {
        if (dataSet.data.deidentifie_id === parseInt(record.deidentifie_id)) {
          dataSet.data = extractKeyValuePairs(record, desiredFieldsAfterSubmission);
        }
      });
    });
    return originalDataSet;
  }

  function processDataForSubmission(records) {
    return records.map((record) => extractKeyValuePairs(record.data, desired_record_fields));
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValidatedData()) {
      const records = [...patientDataArr, ...addPatientDataRows];
      const filteredRecords = processDataForSubmission(records);
      submitRecords(filteredRecords, records);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PatientsInformationTable
        patientData={patientDataArr}
        addPatientDataRows={addPatientDataRows}
        onChange={onAddAddPatientDataFormChange}
        invalidAddRowKeys={invalidAddRowKeys}
        fieldMapping={fieldMapping}
        decimalFields={decimalFields}
      />
      <Stack direction="row" spacing={2} sx={{ marginTop: "10px" }}>
        <Button variant="contained" color="secondary" startIcon={<AddCircleOutlinedIcon />} onClick={handleAddRecord}>
          Add New Record
        </Button>
        {addPatientDataRows.length > 0 && (
          <Button variant="contained" type="submit">Submit</Button>
        )}
      </Stack>
    </form>
  );
};

const generateUniqueKey = (arrayLength) => `${new Date().getTime()}${arrayLength}`;

export default StaticIndividualPatientSection;
